import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Pencil, Phone, Plus, Search, Truck } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { useMe, useRoleQuery } from "@/api/hooks";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { Loading } from "@/components/shared/Loading";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage } from "@/lib/apiError";


const MODES = [
  "Air Import", "Air Export", "Sea FCL Import", "Sea FCL Export",
  "Sea LCL Import", "Sea LCL Export", "Land Import", "Land Export",
  "Courier", "Customs", "Warehousing",
];

const TYPES = [
  ["AIRLINE", "Airline"],
  ["SHIPPING_LINE", "Shipping line"],
  ["TRANSPORTER", "Transporter"],
  ["CUSTOMS_CHA", "Customs / CHA"],
  ["WAREHOUSE", "Warehouse"],
  ["FREIGHT_AGENT", "Freight agent"],
  ["COURIER", "Courier"],
  ["OTHER", "Other"],
];

const EMPTY = {
  name: "", contactName: "", email: "", phone: "", supplierType: "OTHER",
  modes: [], lanes: "", notes: "", active: true, approvalStatus: "PENDING",
};

const typeLabel = (value) => TYPES.find(([key]) => key === value)?.[1] || value || "Other";
const compactDate = (value) => value ? new Date(value).toLocaleDateString() : "—";


function SupplierDialog({ open, onOpenChange, supplier, canApprove }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(supplier ? {
      name: supplier.name || "",
      contactName: supplier.contactName || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      supplierType: supplier.supplierType || "OTHER",
      modes: supplier.modes || [],
      lanes: (supplier.lanes || []).join(", "),
      notes: supplier.notes || "",
      active: supplier.active !== false,
      approvalStatus: supplier.approvalStatus || "PENDING",
    } : EMPTY);
  }, [open, supplier]);

  const set = (field) => (event) => setForm((current) => ({
    ...current,
    [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  }));

  const toggleMode = (mode) => setForm((current) => ({
    ...current,
    modes: current.modes.includes(mode)
      ? current.modes.filter((item) => item !== mode)
      : [...current.modes, mode],
  }));

  const valid = form.name.trim() && (form.email.trim() || form.phone.trim());

  const save = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      contactName: form.contactName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      supplierType: form.supplierType,
      modes: form.modes,
      lanes: form.lanes.split(",").map((item) => item.trim()).filter(Boolean),
      notes: form.notes.trim(),
      active: form.active,
    };
    if (canApprove) payload.approvalStatus = form.approvalStatus;
    try {
      const { data } = supplier
        ? await api.patch(`/vendors/${supplier.id}`, { ...payload, expectedVersion: supplier.version || 0 })
        : await api.post("/vendors", payload);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["supplier-directory"] }),
        qc.invalidateQueries({ queryKey: ["vendors"] }),
        qc.invalidateQueries({ queryKey: ["ops-suppliers"] }),
      ]);
      toast.success(supplier ? "Supplier record updated" : "Supplier added to the directory");
      if (!canApprove && data.approvalStatus === "PENDING") {
        toast.info("Management approval is required before this supplier can receive rate requests or bookings.");
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Supplier record could not be saved."));
      if (error.response?.status === 409) {
        qc.invalidateQueries({ queryKey: ["supplier-directory"] });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !busy && onOpenChange(value)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{supplier ? "Edit supplier" : "Add supplier"}</DialogTitle>
          <DialogDescription>
            Maintain one controlled supplier record for rate requests, booking, and Accounts Payable.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label htmlFor="supplier-name">Legal / trading name *</Label><Input id="supplier-name" value={form.name} onChange={set("name")} className="mt-1.5" autoFocus /></div>
            <div><Label htmlFor="supplier-contact">Primary contact</Label><Input id="supplier-contact" value={form.contactName} onChange={set("contactName")} className="mt-1.5" /></div>
            <div><Label htmlFor="supplier-type">Supplier type</Label><select id="supplier-type" value={form.supplierType} onChange={set("supplierType")} className="mt-1.5 h-9 w-full rounded-md border border-input bg-white px-3 text-sm">{TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            <div><Label htmlFor="supplier-email">Email</Label><Input id="supplier-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
            <div><Label htmlFor="supplier-phone">Phone / WhatsApp</Label><Input id="supplier-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
          </div>
          <p className="text-xs text-slate-500">Provide at least one contact method so the rate or booking request has a real recipient.</p>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Service modes</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {MODES.map((mode) => <label key={mode} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700"><input type="checkbox" checked={form.modes.includes(mode)} onChange={() => toggleMode(mode)} className="h-4 w-4 rounded border-slate-300" />{mode}</label>)}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label htmlFor="supplier-lanes">Key lanes</Label><Input id="supplier-lanes" value={form.lanes} onChange={set("lanes")} placeholder="e.g. Shanghai, Jebel Ali, GCC" className="mt-1.5" /><p className="mt-1 text-[11px] text-slate-500">Separate lanes with commas.</p></div>
            <div className="sm:col-span-2"><Label htmlFor="supplier-notes">Internal notes</Label><Textarea id="supplier-notes" value={form.notes} onChange={set("notes")} rows={3} className="mt-1.5" /></div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={form.active} onChange={set("active")} className="h-4 w-4 rounded border-slate-300" />Active supplier</label>
            {canApprove && <div><Label htmlFor="supplier-approval">Approval status</Label><select id="supplier-approval" value={form.approvalStatus} onChange={set("approvalStatus")} className="mt-1.5 h-9 w-full rounded-md border border-input bg-white px-3 text-sm"><option value="PENDING">Pending review</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option></select></div>}
          </div>

          {!canApprove && <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Management controls supplier approval. Material changes to an approved supplier return it to Pending review.</div>}
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button type="submit" disabled={!valid || busy}>{busy ? "Saving…" : supplier ? "Save changes" : "Add supplier"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export default function Suppliers() {
  const [search, setSearch] = useState("");
  const [approval, setApproval] = useState("ALL");
  const [activity, setActivity] = useState("ALL");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const { data: me } = useMe();
  const { data: suppliers, error } = useRoleQuery(["supplier-directory"], "/supplier-directory");
  const canManage = Boolean(me?.caps?.manage_suppliers);
  const canApprove = Boolean(me?.caps?.approve_suppliers);

  const filtered = useMemo(() => (suppliers || []).filter((supplier) => {
    const haystack = [supplier.id, supplier.name, supplier.contactName, supplier.email, supplier.phone, supplier.supplierType, ...(supplier.modes || []), ...(supplier.lanes || [])].join(" ").toLowerCase();
    if (search && !haystack.includes(search.toLowerCase())) return false;
    if (approval !== "ALL" && supplier.approvalStatus !== approval) return false;
    if (activity === "ACTIVE" && supplier.active === false) return false;
    if (activity === "INACTIVE" && supplier.active !== false) return false;
    return true;
  }), [suppliers, search, approval, activity]);

  if (!suppliers && !error) return <Loading />;
  if (error) return <div className="p-6 text-sm text-slate-600">Supplier master access is not available for this role.</div>;

  const openNew = () => { setSelected(null); setDialogOpen(true); };
  const openEdit = (supplier) => { setSelected(supplier); setDialogOpen(true); };
  const approvedCount = suppliers.filter((supplier) => supplier.active !== false && supplier.approvalStatus === "APPROVED").length;
  const pendingCount = suppliers.filter((supplier) => supplier.approvalStatus === "PENDING").length;

  return (
    <>
      <PageHeader
        title="Supplier Directory"
        subtitle={`${approvedCount} active approved · ${pendingCount} pending review · shared by Sales, Operations and Finance`}
        actions={<div className="flex flex-wrap items-center gap-2"><div className="relative"><Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search suppliers" className="h-8 w-56 bg-white pl-8" /></div>{canManage && <Button size="sm" onClick={openNew}><Plus className="mr-1.5 h-4 w-4" />Add supplier</Button>}</div>}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select value={approval} onChange={(event) => setApproval(event.target.value)} aria-label="Filter by approval status" className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"><option value="ALL">All approval statuses</option><option value="APPROVED">Approved</option><option value="PENDING">Pending review</option><option value="REJECTED">Rejected</option></select>
          <select value={activity} onChange={(event) => setActivity(event.target.value)} aria-label="Filter by active status" className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"><option value="ALL">Active and inactive</option><option value="ACTIVE">Active only</option><option value="INACTIVE">Inactive only</option></select>
          {!canManage && <span className="text-xs text-slate-500">Read-only vendor-master visibility for Accounts Payable and control review.</span>}
        </div>

        {filtered.length ? <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[980px] text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><th className="px-4 py-3 text-left font-semibold">Supplier</th><th className="px-4 py-3 text-left font-semibold">Contact</th><th className="px-4 py-3 text-left font-semibold">Services / lanes</th><th className="px-4 py-3 text-left font-semibold">Approval</th><th className="px-4 py-3 text-left font-semibold">Activity</th><th className="px-4 py-3 text-left font-semibold">Last updated</th>{canManage && <th className="px-4 py-3 text-right font-semibold">Action</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((supplier) => <tr key={supplier.id} className="hover:bg-slate-50/80">
                <td className="px-4 py-3"><div className="flex items-start gap-3"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Truck className="h-4 w-4" /></div><div><div className="font-semibold text-slate-900">{supplier.name}</div><div className="mt-0.5 text-[11px] text-slate-500">{supplier.id} · {typeLabel(supplier.supplierType)}</div>{supplier.contactName && <div className="mt-1 text-xs text-slate-600">{supplier.contactName}</div>}</div></div></td>
                <td className="px-4 py-3"><div className="space-y-1 text-xs text-slate-600">{supplier.email && <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{supplier.email}</div>}{supplier.phone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{supplier.phone}</div>}</div></td>
                <td className="max-w-[330px] px-4 py-3"><div className="flex flex-wrap gap-1">{(supplier.modes || []).slice(0, 4).map((mode) => <span key={mode} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">{mode}</span>)}{(supplier.modes || []).length > 4 && <span className="text-[11px] text-slate-500">+{supplier.modes.length - 4}</span>}</div><div className="mt-1.5 truncate text-[11px] text-slate-500">{(supplier.lanes || []).join(" · ") || "No preferred lanes recorded"}</div></td>
                <td className="px-4 py-3"><StatusPill status={supplier.approvalStatus === "PENDING" ? "Pending review" : supplier.approvalStatus} bucket={supplier.approvalStatus === "APPROVED" ? "success" : supplier.approvalStatus === "REJECTED" ? "danger" : "warning"} /></td>
                <td className="px-4 py-3"><StatusPill status={supplier.active === false ? "Inactive" : "Active"} bucket={supplier.active === false ? "neutral" : "info"} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">{compactDate(supplier.updatedAt || supplier.createdAt)}<div className="mt-0.5 text-[11px] text-slate-400">v{supplier.version || 0}</div></td>
                {canManage && <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(supplier)}><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button></td>}
              </tr>)}
            </tbody>
          </table>
        </div> : <div className="rounded-xl border border-slate-200 bg-white"><EmptyState title="No suppliers match these filters" sub={suppliers.length ? "Adjust the search or status filters." : canManage ? "Add a real supplier before requesting rates or creating a booking." : "No supplier records are available."} /></div>}
      </div>
      {canManage && <SupplierDialog open={dialogOpen} onOpenChange={setDialogOpen} supplier={selected} canApprove={canApprove} />}
    </>
  );
}

